import { motion } from "framer-motion";
import { ChevronRight, Flame } from "lucide-react";
import { Link } from "react-router-dom";
import { SectionHead, Reveal, staggerItem } from "../Reveal";
import { getCategory, getCourse, getCoursesByCategory } from "../../data/courses";
import { IMAGES, whatsappLink } from "../../data/site";

const ACCENTS = {
  sky: {
    text: "text-sky-400",
    border: "border-sky-500/30",
    hover: "hover:border-sky-400/60 hover:shadow-[0_0_30px_rgba(56,189,248,0.15)]",
    bg: "bg-sky-500/10",
    line: "bg-sky-400",
  },
  blue: {
    text: "text-blue-400",
    border: "border-blue-500/30",
    hover: "hover:border-blue-400/60 hover:shadow-[0_0_30px_rgba(59,130,246,0.18)]",
    bg: "bg-blue-500/10",
    line: "bg-blue-400",
  },
  orange: {
    text: "text-orange-400",
    border: "border-orange-500/30",
    hover: "hover:border-orange-400/60 hover:shadow-[0_0_30px_rgba(249,115,22,0.15)]",
    bg: "bg-orange-500/10",
    line: "bg-orange-400",
  },
  emerald: {
    text: "text-emerald-400",
    border: "border-emerald-500/30",
    hover: "hover:border-emerald-400/60 hover:shadow-[0_0_30px_rgba(52,211,153,0.15)]",
    bg: "bg-emerald-500/10",
    line: "bg-emerald-400",
  },
};

const CAT_IMAGES = { network: IMAGES.datacenter, cloud: IMAGES.cloud, cyber: IMAGES.cyber, ai: IMAGES.ai };
const CAT_IDS = ["network", "cloud", "cyber", "ai"];

export const CourseCategories = ({ showAll = false }) => {
  const combo = getCourse("interview-mastery-combo");
  return (
    <section id="courses" data-testid="courses-section" className="relative py-20 sm:py-28 bg-[#04070F]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow="Course Catalog"
          title={
            <>
              Four Tracks. <span className="text-gradient-sky">33 Courses.</span>{" "}
              <span className="text-gradient-orange">One Goal: Get Hired.</span>
            </>
          }
          sub="Every course is 1-on-1 live with a working engineer, includes recordings, 24x7 labs, notes PDF and mock interviews."
        />

        {combo && (
          <Reveal className="mb-10">
            <Link
              to={`/courses/${combo.slug}`}
              data-testid="combo-banner"
              className="group relative block rounded-2xl border border-orange-500/50 bg-gradient-to-r from-orange-500/15 via-[#0B1226] to-blue-700/20 p-6 sm:p-8 overflow-hidden hover:border-orange-400 hover:shadow-[0_0_40px_rgba(249,115,22,0.2)] transition-all duration-300"
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
                <div className="flex-1">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-500 text-white font-display text-[10px] font-bold px-3.5 py-1 uppercase tracking-wider mb-3">
                    <Flame className="w-3 h-3" /> Most Selling Combo
                  </span>
                  <h3 className="font-display text-xl sm:text-2xl font-bold text-white group-hover:text-orange-300 transition-colors duration-200">
                    {combo.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-300 max-w-2xl">
                    Technical + HR mock interviews for all tracks, resume & LinkedIn makeover, salary negotiation — everything between you and the offer letter.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-slate-400 font-mono2">
                    <span className="rounded border border-sky-500/25 px-2.5 py-1">Resume + LinkedIn</span>
                    <span className="rounded border border-sky-500/25 px-2.5 py-1">Technical Mock Rounds</span>
                    <span className="rounded border border-sky-500/25 px-2.5 py-1">HR + Salary Negotiation</span>
                    <span className="rounded border border-sky-500/25 px-2.5 py-1">All 3 Tracks Covered</span>
                  </div>
                </div>
                <span className="shrink-0 inline-flex items-center gap-2 rounded-xl bg-orange-500 group-hover:bg-orange-600 text-white font-display font-bold text-sm px-6 py-3.5 transition-colors duration-200">
                  View Combo <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                </span>
              </div>
            </Link>
          </Reveal>
        )}

        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-6">
          {CAT_IDS.map((catId, colIdx) => {
            const cat = getCategory(catId);
            const a = ACCENTS[cat.accent];
            const courses = getCoursesByCategory(catId);
            return (
              <motion.div
                key={catId}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: colIdx * 0.12, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`category-column-${catId}`}
                className={`rounded-2xl border ${a.border} bg-[#0B1226] overflow-hidden flex flex-col`}
              >
                <div className="relative h-36">
                  <img src={CAT_IMAGES[catId]} alt={cat.name} className="w-full h-full object-cover opacity-40" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1226] via-[#0B1226]/60 to-transparent" />
                  <div className="absolute bottom-4 left-5 right-5">
                    <span className={`font-mono2 text-[10px] uppercase tracking-[0.25em] ${a.text} font-semibold`}>
                      Track {String(colIdx + 1).padStart(2, "0")}
                    </span>
                    <h3 className="font-display text-xl font-bold text-white mt-1">{cat.name}</h3>
                    <p className="text-[11px] text-slate-400 mt-0.5">{cat.tagline}</p>
                  </div>
                </div>

                <div className="p-4 flex-1 flex flex-col gap-2.5">
                  {courses.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/courses/${c.slug}`}
                      data-testid={`course-card-${c.slug}`}
                      className={`group relative flex items-center gap-3 rounded-xl border border-transparent ${a.border} bg-[#0F1A36]/60 px-4 py-3 ${a.hover} hover:-translate-y-0.5 transition-all duration-300`}
                    >
                      <span className={`absolute left-0 top-3 bottom-3 w-[3px] rounded-full ${a.line} opacity-60 group-hover:opacity-100 transition-opacity duration-300`} />
                      <div className="flex-1 min-w-0 pl-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-display text-sm font-semibold text-white group-hover:text-sky-300 transition-colors duration-200 leading-snug">
                            {c.title}
                          </h4>
                          {c.tag && (
                            <span className={`shrink-0 font-mono2 text-[9px] uppercase tracking-wider ${a.text} ${a.bg} rounded px-1.5 py-0.5`}>
                              {c.tag}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-500 mt-1">
                          {c.level} • {c.duration} • 1-on-1 Live
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 shrink-0 text-slate-600 group-hover:text-white group-hover:translate-x-1 transition-all duration-300" />
                    </Link>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>

        {!showAll && (
          <div className="text-center mt-12">
            <Link
              data-testid="view-all-courses-btn"
              to="/courses"
              className="inline-flex items-center gap-2 rounded-xl border border-sky-500/40 text-sky-300 hover:text-white hover:border-sky-400 hover:bg-sky-500/10 font-display font-semibold text-sm px-8 py-3.5 transition-all duration-300"
            >
              View Full Course Catalog
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};
